import useResizeObserver from "@react-hook/resize-observer";
import { init, dispose } from "echarts";
import { useRef, useEffect } from "react";

import {
  useChartBaseUtils,
  reverseLegendSelected,
  getSelectedValuesFromSelectChanged,
} from "./ChartBaseUtils";

const ChartBase = ({
  options = {},
  loading = false,
  fetching = false,
  reverseLegendSelection = false,
  toggleLegendSelectOnPieClick = true,
  toggleSelectOnXAxisLabelClick = true,
  onLegendSelectChange,
  onSelectChange,
  minHeight = "200px",
  style,
  className,
  id,
}) => {
  const chartRef = useRef(null);
  const divProps = { id, className };

  const {
    getChart,
    getOptions,
    resizeChart,
    setOptions,
    setLoading,
    dispatchAction,
  } = useChartBaseUtils(chartRef);

  useResizeObserver(chartRef, resizeChart);

  const shouldSelectOnPieClick =
    toggleLegendSelectOnPieClick && onLegendSelectChange;

  const shouldSelectOnXAxisLabelClick =
    toggleSelectOnXAxisLabelClick && onSelectChange;

  const shouldListenClick =
    shouldSelectOnPieClick || shouldSelectOnXAxisLabelClick;

  const onLegendSelectChanged = (params) => {
    if (!onLegendSelectChange) return;

    const selected = reverseLegendSelection
      ? reverseLegendSelected(params)
      : params.selected;

    onLegendSelectChange({ ...params, selected });
  };

  const onSelectChanged = (params) => {
    if (!onSelectChange) return;

    const options = getOptions();
    const selected = getSelectedValuesFromSelectChanged(params, options);

    onSelectChange({ ...params, selected });
  };

  const onClick = (params) => {
    if (!shouldListenClick) return;

    const { componentType, targetType, seriesType, name, dataIndex } = params;

    if (
      shouldSelectOnXAxisLabelClick &&
      componentType === "xAxis" &&
      targetType === "axisLabel"
    ) {
      dispatchAction({ type: "toggleSelect", dataIndex });
    }

    if (
      shouldSelectOnPieClick &&
      componentType === "series" &&
      seriesType === "pie"
    ) {
      dispatchAction({ type: "legendToggleSelect", name });
    }
  };

  const setCallbacks = () => {
    const chart = getChart();
    if (!chart) return;

    if (onLegendSelectChange)
      chart.on("legendselectchanged", onLegendSelectChanged);
    if (onSelectChange) chart.on("selectchanged", onSelectChanged);
    if (shouldListenClick) chart.on("click", onClick);
  };

  const cancelCallbacks = () => {
    const chart = getChart();
    if (!chart) return;

    // Do not specify 2nd param `handler` to make sur all
    // event functions of this type will be unbound.
    chart.off("legendselectchanged");
    chart.off("selectchanged");
    chart.off("click");
  };

  useEffect(() => {
    if (!chartRef.current) return;

    // Initialize chart & init callbacks on mount
    const chart = init(chartRef.current, null, { renderer: "svg" });
    setCallbacks();

    // Cleanup on unmount
    return () => {
      cancelCallbacks();
      chart.clear();
      dispose(chart);
    };
  }, []);

  // Update chart options
  useEffect(() => {
    setOptions(options);
  }, [options]);

  // Update chart loading
  useEffect(() => {
    setLoading(loading);
  }, [loading]);

  // Update chart loading
  useEffect(() => {
    if (loading || fetching) return;

    // On both loading & fetching finished, reset callbacks to make sure
    // there data is synced. We have to do this because we are handling
    // DOM callbacks here and they do not update with life cycle.

    cancelCallbacks();
    setCallbacks();
  }, [loading, fetching]);

  return (
    <div
      {...divProps}
      ref={chartRef}
      style={{ width: "100%", height: "100%", minHeight, ...style }}
    />
  );
};

export default ChartBase;
